/**
 * React Query hooks for dashboards.
 * React Query hooks for the corresponding entity.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { FarmerProgrammeDashboard } from "./farmerDashboardsTypes";

// Re-export types for convenience
export { dashboardApi };
export type { FarmerProgrammeDashboard };

export type DashboardMetadata = {
  guid: string;
  title: string;
  programmeId?: string;
};

export type AccessibleFarmerDashboard = {
  guid: string;
  name: string;
  dashboards: { visualizationId: string; title: string }[];
};

export type AccessibleFarmerProgramme = {
  userGuid: string;
  userName: string;
  userEmail: string;
  joinedDate: string | null;
  consentSigned: boolean;
  programmes: { guid: string; name: string; description: string }[];
};

class FarmerDashboardAPI {
  baseUrl: string;

  constructor() {
    this.baseUrl = `${import.meta.env.VITE_API_ENDPOINT}/v1/`;
  }

  async getFarmerDashboardIds(): Promise<{ dashboards: DashboardMetadata[] }> {
    const res = await fetch(`${this.baseUrl}getDashboards/farmerDashboardIds`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    });
    return (await res.json()) as { dashboards: DashboardMetadata[] };
  }

  async getProjectDashboardIds(): Promise<{ dashboards: DashboardMetadata[] }> {
    const res = await fetch(
      `${this.baseUrl}getDashboards/corporationsDashboardIds?dashboardType=CORPORATE`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
      }
    );
    // Return empty dashboards array if the endpoint returns 404
    if (res.status === 404) {
      return { dashboards: [] };
    }
    return (await res.json()) as { dashboards: DashboardMetadata[] };
  }

  async getAccessibleFarmerProgrammes(): Promise<AccessibleFarmerProgramme[]> {
    const res = await fetch(
      `${this.baseUrl}dashboardAccess/accessibleFarmers`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
      }
    );
    if (!res.ok) return [];
    return (await res.json()) as AccessibleFarmerProgramme[];
  }

  async getAccessibleFarmerDashboardIds(): Promise<
    AccessibleFarmerDashboard[]
  > {
    const res = await fetch(
      `${this.baseUrl}getDashboards/accessibleFarmerDashboardIds`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
      }
    );
    const data = (await res.json()) as {
      userGuid: string;
      userName: string;
      dashboards: { visualizationId: string; title: string }[];
    }[];
    // Transform API response to match the expected type
    // API returns: { userGuid, userName, dashboards: [...] }[]
    // We transform to: { guid, name, dashboards: [...] }[]
    return data.map((item) => ({
      guid: item.userGuid,
      name: item.userName,
      dashboards: item.dashboards,
    }));
  }
}

const dashboardApi = new FarmerDashboardAPI();

/**
 * Fetch all farmer programme dashboards
 */
export function useFarmerDashboards(enabled = true) {
  return useQuery({
    queryKey: queryKeys.dashboards.farmer,
    queryFn: async () => {
      const response = await dashboardApi.getFarmerDashboardIds();
      return response.dashboards;
    },
    enabled,
  });
}

/**
 * Fetch all project dashboards
 */
export function useProjectDashboards(enabled = true) {
  return useQuery({
    queryKey: queryKeys.dashboards.project,
    queryFn: async () => {
      const response = await dashboardApi.getProjectDashboardIds();
      return response.dashboards;
    },
    enabled,
  });
}

/**
 * Fetch accessible farmers with their programmes (gated by corporationMetabaseUrlMetadata).
 * Returns an empty array for corporate users without farmer-detail access.
 */
export function useAccessibleFarmerProgrammes(enabled = true) {
  return useQuery({
    queryKey: queryKeys.dashboards.accessibleFarmerProgrammes,
    queryFn: async () => {
      return dashboardApi.getAccessibleFarmerProgrammes();
    },
    enabled,
  });
}

/**
 * Fetch all accessible farmer dashboards
 */
export function useAccessibleFarmerDashboards(enabled = true) {
  return useQuery({
    queryKey: queryKeys.dashboards.accessibleFarmer,
    queryFn: async () => {
      return dashboardApi.getAccessibleFarmerDashboardIds();
    },
    enabled,
  });
}

/**
 * Helper to get accessible farmer dashboard by user guid
 */
export function getAccessibleFarmerDashboardByUserId(
  dashboards: AccessibleFarmerDashboard[] | undefined,
  userId: string
): AccessibleFarmerDashboard | undefined {
  return dashboards?.find((d) => d.guid === userId);
}

/**
 * Helper function to get dashboard by ID from array
 */
export function getDashboardByIdFromArray(
  dashboards: DashboardMetadata[] | undefined,
  dashboardId: string
): DashboardMetadata | undefined {
  return dashboards?.find((d) => d.guid === dashboardId);
}

/**
 * Helper function to reset dashboards query cache
 */
export function resetDashboardsQueryCache(
  queryClient: ReturnType<typeof useQueryClient>
): void {
  queryClient.removeQueries({ queryKey: queryKeys.dashboards.farmer });
  queryClient.removeQueries({ queryKey: queryKeys.dashboards.project });
  queryClient.removeQueries({
    queryKey: queryKeys.dashboards.accessibleFarmer,
  });
  queryClient.removeQueries({
    queryKey: queryKeys.dashboards.accessibleFarmerProgrammes,
  });
}

/**
 * Hook wrapper for resetDashboardsQueryCache
 */
export function useResetDashboardsQueryCache() {
  const queryClient = useQueryClient();
  return () => resetDashboardsQueryCache(queryClient);
}

/**
 * Fetch farmer dashboards directly (not a hook, for use in services).
 */
export async function fetchFarmerDashboardsQuery(): Promise<
  DashboardMetadata[]
> {
  const response = await dashboardApi.getFarmerDashboardIds();
  return response.dashboards;
}

/**
 * Fetch project dashboards directly (not a hook, for use in services).
 */
export async function fetchProjectDashboardsQuery(): Promise<
  DashboardMetadata[]
> {
  const response = await dashboardApi.getProjectDashboardIds();
  return response.dashboards;
}

/**
 * Fetch accessible farmer dashboards directly (not a hook, for use in services).
 */
export async function fetchAccessibleFarmerDashboardsQuery(): Promise<
  AccessibleFarmerDashboard[]
> {
  return dashboardApi.getAccessibleFarmerDashboardIds();
}

/**
 * Fetch accessible farmer programmes directly (not a hook, for use in services).
 */
export async function fetchAccessibleFarmerProgrammesQuery(): Promise<
  AccessibleFarmerProgramme[]
> {
  return dashboardApi.getAccessibleFarmerProgrammes();
}
